import React from 'react';
/* Carbon Icons */
import { 
    ArrowLeft,
    ArrowRight,
    Bullhorn,
    Calendar,
    ChartLine,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    Close,
    Download,
    EarthEuropeAfrica,
    Edit,
    Email,
    Filter,
    FitToScreen,
    HeatMap_02,
    Information,
    Location,
    LogoYoutube,
    Map,
    Meter,
    Notification,
    OverflowMenuVertical,
    Pause,
    Pin,
    Play,
    Renew,
    Report,
    Road,
    Satellite,
    Search,
    Settings,
    Share,
    SkipBack,
    SkipForward,
    Stop,
    Time,
    TrafficEvent,
    TriangleDownOutline,
    Upload,
    User,
    VehicleConnected,
    Video,
    View,
    VolumeUpAlt,
    Warning,
    Help,
    WarningAlt,
    CheckmarkOutline,
    WarningMultiple,
    Plug,
    Debug,
    Van,
    Fire,
    Favorite,
    Star,
    Home,
    Locked,
    Unlocked,
    Car,
    Bus,
    Bicycle,
    Cyclist,
    MapBoundary,
    Camera,
    Identification,
    UserAvatar,
    Undo,
    ArrowDown,
    ArrowUpRight,
    BatteryCharging,
    BatteryQuarter,
    BatteryEmpty,
    BatteryFull,
    CloudOffline,
    Cloud,
    Rule,
    CalendarHeatMap,
    Gears,
    Tools,
    CloudDownload,
    ChartBar,
    ChartPie,
    Db2Database,
    TableSplit,
} from '@carbon/icons-react';
/* Custom SVGs */
import CarbonReportSVG from '../../assets/icons/CarbonReportSVG';
import AutoStopLogoColourSVG from '../../assets/icons/AutoStopLogoColourSVG';
import CyclearLogoGreySVG from '../../assets/icons/CyclearLogoGreySVG';
import DriverBehaviourSVG from '../../assets/icons/DriverBehaviourSVG';
import IDClearLogoColourSVG from '../../assets/icons/IDClearLogoColourSVG';
import LowFuelSVG from '../../assets/icons/LowFuelSVG';
import AdvancedGPSAccuracySVG from '../../assets/icons/AdvancedGPSAccuracySVG';
import AutomaticHealthStatusReportsSVG from '../../assets/icons/AutomaticHealthStatusReportsSVG';
import CustomisableWidgetDashboardSVG from '../../assets/icons/CustomisableWidgetDashboardSVG';
import DriverIdentificationSVG from '../../assets/icons/DriverIdentificationSVG';
import EasyReportAndAdminControlSVG from '../../assets/icons/EasyReportAndAdminControlSVG';
import EventReportingSVG from '../../assets/icons/EventReportingSVG';
import FnolSVG from '../../assets/icons/FnolSVG';
import FuelEfficiencySVG from '../../assets/icons/FuelEfficiencySVG';
import FuelOptimisationSVG from '../../assets/icons/FuelOptimisationSVG';
import GeoFencingSVG from '../../assets/icons/GeoFencingSVG';
import ISSInstallationAndServiceQualityAssuranceSVG from '../../assets/icons/ISSInstallationAndServiceQualityAssuranceSVG';
import MapAndAssetDisplaySVG from '../../assets/icons/MapAndAssetDisplaySVG';
import MultiAssetActivitySideBarSVG from '../../assets/icons/MultiAssetActivitySideBarSVG';
import NearestVehicleAndPostcodeSearchSVG from '../../assets/icons/NearestVehicleAndPostcodeSearchSVG';
import OperationalAlertsSVG from '../../assets/icons/OperationalAlertsSVG';
import PTOConnectionGuaranteeSVG from '../../assets/icons/PTOConnectionGuaranteeSVG';
import RealTimeTrackingTelematicsAndVideoSVG from '../../assets/icons/RealTimeTrackingTelematicsAndVideoSVG';
import RealTimeTrafficOverlaySVG from '../../assets/icons/RealTimeTrafficOverlaySVG';
import RemoteTachographDownloadSVG from '../../assets/icons/RemoteTachographDownloadSVG';
import RoadRelatedSpeedingAlertsSVG from '../../assets/icons/RoadRelatedSpeedingAlertsSVG';
import RouteOptimisationSVG from '../../assets/icons/RouteOptimisationSVG';
import RoutePlanningWithLiveTrafficSVG from '../../assets/icons/RoutePlanningWithLiveTrafficSVG';
import RXLiteIntegrationSVG from '../../assets/icons/RXLiteIntegrationSVG';
import UKInHouseDevelopmentTeamSVG from '../../assets/icons/UKInHouseDevelopmentTeamSVG';
import WebBasedTrackingPlatformSVG from '../../assets/icons/WebBasedTrackingPlatformSVG';

const DEFAULT_ICON_SIZE: number = 16;
const DEFAULT_ICON_CLASSNAME: string = 'carbon-icon';

export const DEFAULT_DASHBOARD_ICON_SIZE = 64;
export const DEFAULT_DASHBOARD_ICON_CLASSNAME = 'dashboard-carbon-icon';

/**
 * This function is used to patch over the old icons used in toolbars and a few other places with newer ones. 
 * If possible I recommend using the normal approach in-place e.g. <Email /> instead of using this func.
 * Returns rendered component from IBM Carbon Icons: https://carbondesignsystem.com/elements/icons/library/
 * @param name defined as strings already used in the app, e.g. "events"
 * @param size size of the icon in pixels
 * @returns React.JSX.Element
 */
export const getIbmIconComponent = (
    name: string,
    size: number = DEFAULT_ICON_SIZE,
): React.JSX.Element => {
    switch (name) {
        case "envelope":
            return (
                <Email 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "person":
        case "drivers":
        case "user":
            return (
                <User 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "clock":
            return (
                <Time 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}
                />
        );
        case "arrow-left":
            return (
                <ArrowLeft 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "arrow-right":
            return (
                <ArrowRight 
                    size={size}  
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "assets":
            return (
                <VehicleConnected 
                    size={size}  
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "car":
            return (
                <TrafficEvent 
                    size={size}  
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "calendar":
            return (
                <Calendar 
                    size={size}  
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "data":
            return (
                <ChartLine 
                    size={size}  
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "download":
            return (
                <Download 
                    size={size}  
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "earth":
            return (
                <EarthEuropeAfrica 
                    size={size}  
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "events":
        case "bell":
            return (
                <Notification 
                    size={size}  
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "fire":
            return (
                <HeatMap_02 
                    size={size}  
                    className={DEFAULT_ICON_CLASSNAME}
                />
            );
        case "hamb-with-dots":
        case "funnel":
            return (
                <Filter 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "info":
            return (
                <Information 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "location":
        case "target":
            return (
                <Location 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "magnifier":
            return (
                <Search 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "map":
            return (
                <Map 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "pin-down-left":
        case "pin-down":
        case "pin-left":
            return (
                <Pin 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "reports":
            return (
                <Report 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "road":
            return (
                <Road 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "satellite":
            return (
                <Satellite  
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "speedometer":
            return (
                <Meter 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "triangle-down":
            return (
                <TriangleDownOutline  
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "video":
            return (
                <Video 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "warning":
            return (
                <Warning 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "youtube":
            return (
                <LogoYoutube
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "chevron-down":
            return (
                <Filter 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "chevron-left":
            return (
                <ChevronLeft 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "chevron-right":
            return (
                <ChevronRight 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "chevron-up":
            return (
                <ChevronUp 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "cross":
            return (
                <Close 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "eye":
            return (
                <View 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "menu":
            return (
                <OverflowMenuVertical 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "pencil":
            return (
                <Edit 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "refresh":
            return (
                <Renew 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "share":
        case "share2":
            return (
                <Share 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "upload":
            return (
                <Upload 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "fast-forward":
            return (
                <SkipForward
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "fast-rewind":
            return (
                <SkipBack
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "fullscreen":
            return (
                <FitToScreen 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "play-arrow":
        case "play":
            return (
                <Play 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "volume-up":
            return (
                <VolumeUpAlt 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "settings":
            return (
                <Settings 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "pause":
            return (
                <Pause 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "stop":
            return (
                <Stop 
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        case "alert":
            return (
                <Bullhorn
                    size={size} 
                    className={DEFAULT_ICON_CLASSNAME}    
                />
            );
        default:
            return (<></>);
    }
}

/**
 * This function patches over the old dashboard icons. 
 * Do not use this elsewhere, prefer improting icons directly where you need them.
 * If updating also update IconPicker.jsx icons.
 * id 17 and upwards are custom icon SVGs.
 * @returns Object
 */
export const getChartIconList = (): Object => {
    const icons = [
        {
            id: 1,
            title: (
                <Time 
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 2,
            title: (
                <Information 
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}    
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 3,
            title: (
                <Help 
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}    
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 4,
            title: (
                <WarningAlt 
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}    
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 5,
            title: (
                <CheckmarkOutline 
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}    
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 6,
            title: (
                <WarningMultiple
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 7,
            title: (
                <Plug
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 8,
            title: (
                <Debug
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 9,
            title: (
                <Video
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 10,
            title: (
                <Van
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 11,
            title: (
                <Meter
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 12,
            title: (
                <Location
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 13,
            title: (
                <Fire
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 14,
            title: (
                <Favorite
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 15,
            title: (
                <Star
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 16,
            title: (
                <Home
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 17,
            title: CarbonReportSVG,
            isCustomSvg: true,
        },
        {
            id: 18,
            title: AutoStopLogoColourSVG,
            isCustomSvg: true,
        },
        {
            id: 19,
            title: CyclearLogoGreySVG,
            isCustomSvg: true,
        },
        {
            id: 20,
            title: DriverBehaviourSVG,
            isCustomSvg: true,
        },
        {
            id: 21,
            title: IDClearLogoColourSVG,
            isCustomSvg: true,
        },
        {
            id: 22,
            title: LowFuelSVG,
            isCustomSvg: true,
        },
        {
            id: 23,
            title: AdvancedGPSAccuracySVG,
            isCustomSvg: true,
        },
        {
            id: 24,
            title: (
                <Locked
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: true,
        },
        {
            id: 25,
            title: AutomaticHealthStatusReportsSVG,
            isCustomSvg: true,
        },
        {
            id: 26,
            title: CustomisableWidgetDashboardSVG,
            isCustomSvg: true,
        },
        {
            id: 27,
            title: DriverIdentificationSVG,
            isCustomSvg: true,
        },
        {
            id: 28,
            title: EasyReportAndAdminControlSVG,
            isCustomSvg: true,
        },
        {
            id: 29,
            title: EventReportingSVG,
            isCustomSvg: true,
        },
        {
            id: 30,
            title: FnolSVG,
            isCustomSvg: true,
        },
        {
            id: 31,
            title: FuelEfficiencySVG,
            isCustomSvg: true,
        },
        {
            id: 32,
            title: FuelOptimisationSVG,
            isCustomSvg: true,
        },
        {
            id: 33,
            title: GeoFencingSVG,
            isCustomSvg: true,
        },
        {
            id: 34,
            title: ISSInstallationAndServiceQualityAssuranceSVG,
            isCustomSvg: true,
        },
        {
            id: 35,
            title: MapAndAssetDisplaySVG,
            isCustomSvg: true,
        },
        {
            id: 36,
            title: MultiAssetActivitySideBarSVG,
            isCustomSvg: true,
        },
        {
            id: 37,
            title: NearestVehicleAndPostcodeSearchSVG,
            isCustomSvg: true,
        },
        {
            id: 38,
            title: OperationalAlertsSVG,
            isCustomSvg: true,
        },
        {
            id: 39,
            title: PTOConnectionGuaranteeSVG,
            isCustomSvg: true,
        },
        {
            id: 40,
            title: RealTimeTrackingTelematicsAndVideoSVG,
            isCustomSvg: true,
        },
        {
            id: 41,
            title: RealTimeTrafficOverlaySVG,
            isCustomSvg: true,
        },
        {
            id: 42,
            title: RemoteTachographDownloadSVG,
            isCustomSvg: true,
        },
        {
            id: 43,
            title: RoadRelatedSpeedingAlertsSVG,
            isCustomSvg: true,
        },
        {
            id: 44,
            title: RouteOptimisationSVG,
            isCustomSvg: true,
        },
        {
            id: 45,
            title: RoutePlanningWithLiveTrafficSVG,
            isCustomSvg: true,
        },
        {
            id: 46,
            title: RXLiteIntegrationSVG,
            isCustomSvg: true,
        },
        {
            id: 47,
            title: UKInHouseDevelopmentTeamSVG,
            isCustomSvg: true,
        },
        {
            id: 48,
            title: WebBasedTrackingPlatformSVG,
            isCustomSvg: true,
        },
        {
            id: 49,
            title: (
                <Unlocked
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 50,
            title: (
                <Car
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 51,
            title: (
                <Bus
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 52,
            title: (
                <Bicycle
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 53,
            title: (
                <Cyclist
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 54,
            title: (
                <Map
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 55,
            title: (
                <MapBoundary
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 56,
            title: (
                <Camera
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 57,
            title: (
                <Notification
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 58,
            title: (
                <Stop
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 59,
            title: (
                <Identification
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 60,
            title: (
                <User
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 61,
            title: (
                <UserAvatar
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 62,
            title: (
                <Pause
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 63,
            title: (
                <Undo
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 64,
            title: (
                <ArrowDown
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 65,
            title: (
                <ArrowRight
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 66,
            title: (
                <ArrowUpRight
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 67,
            title: (
                <BatteryCharging
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 68,
            title: (
                <BatteryQuarter
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 69,
            title: (
                <BatteryEmpty
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 70,
            title: (
                <BatteryFull
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 71,
            title: (
                <Cloud
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 72,
            title: (
                <CloudOffline
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 73,
            title: (
                <Rule
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 74,
            title: (
                <Calendar
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 75,
            title: (
                <CalendarHeatMap
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 76,
            title: (
                <Settings
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 77,
            title: (
                <Gears
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 78,
            title: (
                <Tools
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 79,
            title: (
                <Download
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 80,
            title: (
                <CloudDownload
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 81,
            title: (
                <Upload
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 82,
            title: (
                <Search
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 83,
            title: (
                <Filter
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 84,
            title: (
                <Renew
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 85,
            title: (
                <ChartLine
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 86,
            title: (
                <ChartBar
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 87,
            title: (
                <ChartPie
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 88,
            title: (
                <TableSplit
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
        {
            id: 89,
            title: (
                <Db2Database
                    size={DEFAULT_DASHBOARD_ICON_SIZE} 
                    className={DEFAULT_DASHBOARD_ICON_CLASSNAME}  
                />
            ),
            isCustomSvg: false,
        },
    ];
    return icons.reduce((a, { id, title, isCustomSvg }) => {
        a[id] = {
            title,
            isCustomSvg,
        };
        return a;
    }, Object.create(null));

}